import { Helmet } from 'react-helmet';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useParams } from 'react-router';
import Lottie from 'react-lottie';
import { useTypedNavigate } from 'hooks/useTypedNavigate';
import * as R from 'Router/routes';
import { Typography } from 'components';
import {
  usePSD2ProxyStatusQuery,
  usePSD2ProxySessionsDownloadMutation,
  usePSD2OfflineSessionsDownloadMutation,
  usePSD2OfflineStatusQuery,
  useClientQuery,
} from 'hooks/webapi';
import BankDocAnimation from 'assets/lottie/bank-doc-animation.json';
import H1 from 'components/H/H1';
import useError from 'hooks/useError';
import useNavigateSearch from 'hooks/useNavigateSearch';

const BankDocLottieOptions = {
  loop: true,
  autoplay: true,
  animationData: BankDocAnimation,
  rendererSettings: {
    preserveAspectRatio: 'xMidYMid slice',
  },
};

const PSD2_ERRORS = [
  'ERROR',
  'NAME_SURNAME_MISMATCH',
  'INVALID_ACCOUNT_TYPE',
  'OUT_OF_RANGE',
  'WRONG_STATEMENT_AMOUNT',
] as const;

type PSD2ErrorType = (typeof PSD2_ERRORS)[number];

function PSD2Receiving() {
  const { t } = useTranslation('psd2_receiving');
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const { showError } = useError();
  const { navigateWithState } = useTypedNavigate();
  const navigateSearch = useNavigateSearch();
  const { data: client } = useClientQuery(true);
  const isOffline = location.pathname.includes(R.OFFLINE_PSD2_RECEIVING);

  const { mutateAsync: downloadHistory } =
    usePSD2ProxySessionsDownloadMutation();
  const { mutate: offlineDownloadHistory } =
    usePSD2OfflineSessionsDownloadMutation();

  const {
    data: statusData,
    isSuccess: isStatusSuccess,
    isFetchedAfterMount,
  } = usePSD2ProxyStatusQuery({
    enabled: !isOffline,
    refetchOnMount: true,
  });
  const {
    data: offlineStatusData,
    isFetchedAfterMount: isFetchedOfflineAfterMount,
  } = usePSD2OfflineStatusQuery(params.token, {
    enabled: isOffline,
    refetchOnMount: true,
  });

  const downloadHistoryRequestRef = useRef<boolean>(false);

  // Handle offline token redirect
  useEffect(() => {
    if (isOffline && !params.token) {
      const sessionToken = sessionStorage.getItem('offline-psd2-token');
      if (!sessionToken) {
        showError(new Error('V URL chybí token. Zavřete okno a zkuste znovu.'));
        return;
      }
      navigateSearch(`${R.OFFLINE_PSD2_RECEIVING}/${sessionToken}`, {
        replace: true,
      });
    }
  }, [isOffline, params.token, navigateSearch, showError]);

  // Handle download history
  useEffect(() => {
    if (isOffline && !params.token) return;

    if (!downloadHistoryRequestRef.current) {
      if (isOffline && params.token) {
        downloadHistoryRequestRef.current = true;
        offlineDownloadHistory(params.token);
        return;
      }

      if (
        !isStatusSuccess ||
        statusData?.typeOfService === 'PDF_PARSER' ||
        !isFetchedAfterMount
      ) {
        return;
      }

      downloadHistoryRequestRef.current = true;
      downloadHistory();
    }
  }, [
    isStatusSuccess,
    statusData,
    isOffline,
    params.token,
    downloadHistory,
    offlineDownloadHistory,
  ]);

  // Handle status changes and navigation
  useEffect(() => {
    if (isOffline) {
      if (!offlineStatusData?.transactionHistory || !isFetchedOfflineAfterMount)
        return;

      if (offlineStatusData.transactionHistory === 'DONE') {
        const sessionToken = sessionStorage.getItem('offline-psd2-token');

        navigateSearch(
          `${R.OFFLINE_PSD2_VERIFIED}/${sessionToken}?status=${offlineStatusData.transactionHistory}`,
          { replace: true },
        );
        return;
      }

      if (
        PSD2_ERRORS.includes(
          String(offlineStatusData.transactionHistory) as PSD2ErrorType,
        )
      ) {
        navigateSearch(R.OFFLINE_PSD2_ERROR);
      }
      return;
    }

    if (!statusData?.transactionHistory || !isFetchedAfterMount) {
      return;
    }

    if (statusData.transactionHistory === 'DONE') {
      navigate(R.PSD2_VERIFIED);
      return;
    }

    if (
      PSD2_ERRORS.includes(
        String(statusData.transactionHistory) as PSD2ErrorType,
      ) &&
      client?.status !== undefined
    ) {
      if (client.status !== 'IDENTIFIED') {
        navigate(R.WELCOME_BACK);
        return;
      }
      navigateWithState(R.PSD2_ERROR, {
        from: 'PSD2Receiving',
        error: {
          typeOfService: statusData.typeOfService,
          status: statusData.status,
          errorReason: statusData.errorReason,
          transactionHistory: statusData.transactionHistory,
        },
      });
    }
  }, [
    statusData,
    offlineStatusData,
    isOffline,
    navigate,
    navigateSearch,
    client?.status,
    isFetchedAfterMount,
    isFetchedOfflineAfterMount,
  ]);

  return (
    <>
      <Helmet>
        <title>{t('html_title')}</title>
      </Helmet>

      <div className="h-full flex flex-col justify-center items-center lg:items-start">
        <H1 dataTest="psd2Receiving-title">{t('page_title')}</H1>

        <Typography dataTest="psd2Receiving-description">
          {t('description')}
        </Typography>

        <div
          className="my-10 mx-auto w-fit"
          data-test="psd2Receiving-lottie-wrapper"
        >
          <Lottie options={BankDocLottieOptions} />
        </div>
      </div>
    </>
  );
}

export default PSD2Receiving;
